import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabaseClient';
import { routeBuilders } from '../routes';
import { slugify } from '../../utils/slug';

interface UserProfileProps {
  profile: {
    display_name: string | null;
    avatar_url?: string | null;
    cover_image_url?: string | null;
    role?: string | null;
    bio?: string | null;
  } | null;
  session: Session;
  onProfileUpdated: () => void;
  onSignOut: () => void;
}

const AVATAR_PLACEHOLDER = 'https://images.unsplash.com/photo-1504593811423-6dd665756598?auto=format&fit=crop&w=800&q=80';
const BIO_LIMIT = 300;

export function UserProfile({ profile, session, onProfileUpdated, onSignOut }: UserProfileProps) {
  const [bio, setBio] = useState(profile?.bio ?? '');
  const [bioStatus, setBioStatus] = useState<string | null>(null);
  const [passwordStatus, setPasswordStatus] = useState<string | null>(null);
  const [avatarStatus, setAvatarStatus] = useState<string | null>(null);
  const [avatarDisplayUrl, setAvatarDisplayUrl] = useState<string | null>(profile?.avatar_url ?? null);
  const [displayName, setDisplayName] = useState(profile?.display_name ?? session.user.email ?? '');

  const [bioSaving, setBioSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [displayNameSaving, setDisplayNameSaving] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    setBio(profile?.bio ?? '');
    setDisplayName(profile?.display_name ?? session.user.email ?? '');
  }, [profile]);

  const extractImageStoragePath = useCallback((raw?: string | null): string | null => {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    if (!trimmed.startsWith('http')) {
      return trimmed.replace(/^\/+/, '');
    }
    const bucketSegment = `/Images/`;
    const bucketIndex = trimmed.indexOf(bucketSegment);
    if (bucketIndex === -1) return null;
    const withoutParams = trimmed.slice(bucketIndex + bucketSegment.length).split('?')[0];
    return decodeURIComponent(withoutParams);
  }, []);

  const resolveAvatarUrl = useCallback(
    async (raw?: string | null): Promise<string | null> => {
      if (!raw) return null;
      const storagePath = extractImageStoragePath(raw);
      if (!storagePath) return raw;
      const { data, error } = await supabase.storage.from('Images').createSignedUrl(storagePath, 60 * 60 * 24 * 7);
      if (error || !data?.signedUrl) {
        console.error('Unable to sign avatar url', error);
        return raw;
      }
      return data.signedUrl;
    },
    [extractImageStoragePath],
  );

  useEffect(() => {
    let isMounted = true;
    resolveAvatarUrl(profile?.avatar_url ?? null).then((url) => {
      if (!isMounted) return;
      setAvatarDisplayUrl(url);
    });
    return () => {
      isMounted = false;
    };
  }, [profile?.avatar_url, resolveAvatarUrl]);

  const handleBioSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session?.user?.id) return;
    setBioStatus(null);
    setBioSaving(true);
    const trimmed = bio.slice(0, BIO_LIMIT);
    try {
      const { error } = await supabase.from('profiles').update({ bio: trimmed }).eq('id', session.user.id);
      if (error) throw error;
      setBioStatus('Bio updated successfully.');
      onProfileUpdated();
    } catch (error: any) {
      setBioStatus(error.message ?? 'Unable to update bio right now.');
    } finally {
      setBioSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setPasswordStatus('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordStatus('Passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    setPasswordStatus(null);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordStatus('Password updated successfully.');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setPasswordStatus(error.message ?? 'Unable to update password right now.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDisplayNameSave = async () => {
    if (!session?.user?.id) return;
    const trimmed = displayName.trim();
    if (!trimmed) {
      setAvatarStatus('Display name cannot be empty.');
      return;
    }
    setDisplayNameSaving(true);
    setAvatarStatus(null);
    try {
      const { error } = await supabase.from('profiles').update({ display_name: trimmed }).eq('id', session.user.id);
      if (error) throw error;
      setAvatarStatus('Display name updated.');
      onProfileUpdated();
    } catch (error: any) {
      setAvatarStatus(error.message ?? 'Unable to update display name right now.');
    } finally {
      setDisplayNameSaving(false);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !session?.user?.id) {
      return;
    }
    setAvatarUploading(true);
    setAvatarStatus(null);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${session.user.id}-${Date.now()}.${ext ?? 'jpg'}`;
      const filePath = `${session.user.id}/${fileName}`;
      const { data: uploadData, error: uploadError } = await supabase.storage.from('Images').upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });
      if (uploadError) throw uploadError;
      const storedPath = uploadData?.path ?? filePath;
      const signedUrl = await resolveAvatarUrl(storedPath);
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: storedPath })
        .eq('id', session.user.id);
      if (updateError) throw updateError;
      setAvatarStatus('Profile photo updated!');
      setAvatarDisplayUrl(signedUrl);
      onProfileUpdated();
    } catch (error: any) {
      setAvatarStatus(error.message ?? 'Unable to upload avatar right now.');
    } finally {
      setAvatarUploading(false);
      event.target.value = '';
    }
  };

  const profileSlug = session.user.id;
  const creatorSlug = useMemo(() => {
    const metaName = profile?.display_name ?? session.user.email?.split('@')[0] ?? session.user.id;
    return slugify(metaName);
  }, [profile?.display_name, session.user.email, session.user.id]);

  const isCreator = (profile?.role ?? '').toLowerCase() === 'creator';

  return (
    <div className="page-shell space-y-8">
      <header className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full overflow-hidden bg-base-200 border border-base-200">
            <img src={avatarDisplayUrl ?? AVATAR_PLACEHOLDER} alt="Avatar" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 flex flex-col gap-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="input input-bordered w-full sm:w-80"
                placeholder="Display name"
              />
              <button
                onClick={handleDisplayNameSave}
                disabled={displayNameSaving}
                className="btn btn-primary"
              >
                {displayNameSaving ? 'Saving...' : 'Save name'}
              </button>
            </div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-base-content cursor-pointer">
                <input type="file" className="hidden" accept="image/*" onChange={handleAvatarUpload} />
                <span className="btn btn-outline btn-primary btn-sm">
                  {avatarUploading ? 'Uploading...' : 'Change photo'}
                </span>
              </label>
              {avatarStatus && <p className="text-sm text-base-content/60">{avatarStatus}</p>}
            </div>
          </div>
        </div>
      </header>

      {isCreator && (
        <section className="grid gap-4 md:grid-cols-2">
          <a
            href={routeBuilders.creatorPublic(creatorSlug)}
            className="rounded-2xl border border-base-200 p-5 hover:border-primary/40 hover:shadow transition-all"
          >
            <p className="text-xs uppercase text-base-content/60 mb-1">Creator</p>
            <h3 className="text-xl font-semibold">My Creator Page</h3>
            <p className="text-sm text-base-content/70 mt-1">View your public creator profile.</p>
          </a>
          <a
            href={routeBuilders.mySeries(profileSlug)}
            className="rounded-2xl border border-base-200 p-5 hover:border-primary/40 hover:shadow transition-all"
          >
            <p className="text-xs uppercase text-base-content/60 mb-1">Series</p>
            <h3 className="text-xl font-semibold">Manage Series</h3>
            <p className="text-sm text-base-content/70 mt-1">Create, edit, and publish your series.</p>
          </a>
        </section>
      )}

      <section className="rounded-md border border-base-200 p-6 space-y-4 shadow-md">
        <div>
          <h2 className="text-xl font-semibold">Bio</h2>
          <p className="text-sm text-base-content/60">Tell listeners a bit about yourself. (max {BIO_LIMIT} characters)</p>
        </div>
        <form onSubmit={handleBioSave} className="space-y-3">
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, BIO_LIMIT))}
            rows={4}
            className="textarea textarea-bordered w-full"
            placeholder="Writer, host, and storyteller..."
          />
          <div className="flex items-center justify-between text-sm text-base-content/60">
            <span>{bio.length}/{BIO_LIMIT}</span>
            {bioStatus && <span>{bioStatus}</span>}
          </div>
          <button
            type="submit"
            disabled={bioSaving}
            className="btn btn-primary"
          >
            {bioSaving ? 'Saving...' : 'Save bio'}
          </button>
        </form>
      </section>

      <section className="rounded-md border border-base-200 p-6 space-y-4 shadow-md">
        <div>
          <h2 className="text-xl font-semibold">Change Password</h2>
          <p className="text-sm text-base-content/60">Keep your account secure with a strong password.</p>
        </div>
        <form onSubmit={handlePasswordChange} className="space-y-3">
          <div>
            <label className="text-sm text-base-content/70 mb-1 block">New password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input input-bordered w-full"
              placeholder="••••••••"
              required
            />
          </div>
          <div>
            <label className="text-sm text-base-content/70 mb-1 block">Confirm password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input input-bordered w-full"
              placeholder="••••••••"
              required
            />
          </div>
          {passwordStatus && <p className="text-sm text-base-content/60">{passwordStatus}</p>}
          <button
            type="submit"
            disabled={passwordSaving}
            className="btn btn-primary"
          >
            {passwordSaving ? 'Updating...' : 'Update password'}
          </button>
        </form>
      </section>

      <section className="rounded-md border border-base-200 p-6 flex justify-between items-center shadow-md">
        <div>
          <h2 className="text-xl font-semibold">Sign out</h2>
          <p className="text-sm text-base-content/60">Log out of your account on this device.</p>
        </div>
        <button
          onClick={onSignOut}
          className="btn btn-outline btn-primary"
        >
          Sign out
        </button>
      </section>
    </div>
  );
}
